import { formatDate } from "@/lib/utils";
import { numberToVietnameseWords } from "@/lib/numberToWords";
import type { Order } from '@/shared/schemas';

import { getCompanyConfig } from "@/config/company";
import { useConfigLookup } from '@/hooks/useConfigLookup';

interface OrderPrintTemplateProps {
    order: Order;
    config?: any;
    showImages?: boolean;
}

import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";
import { formatProductSpecs } from "@/lib/format-specs";
import { orderLineDetailText } from "@/lib/order-detail-text";

const parseSpecs = formatProductSpecs;

function cleanProcessText(processText: string, specsText: string): string {
    if (!processText) return '';
    let cleaned = processText.replace(/\+/g, ', ');
    
    // 1. Remove duplicate specs from process
    if (specsText) {
        const escapedSpecs = specsText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const specRegex = new RegExp(`(?:,\\s*)?${escapedSpecs}(?:\\s*,)?`, 'gi');
        cleaned = cleaned.replace(specRegex, ', ').replace(/,\s*$/, '').replace(/^,\s*/, '').trim();
    }
    
    // 2. Format material names if they match pattern like "I350 Ivory" -> "Ivory 350gsm"
    cleaned = cleaned.replace(/(^|,\s*)([A-Z])(\d{2,3})\s+([a-zA-Z0-9_-]+)/gi, (match, prefix, letter, weight, name) => {
        return `${prefix}${name} ${weight}gsm`;
    });
    
    return cleaned;
}

export default function OrderPrintTemplate({ order, config, showImages = false }: OrderPrintTemplateProps) {
    const c = getCompanyConfig(config);
    const details = (order.orderDetails || []) as any[];
    const now = new Date();
    
    const { options: paymentTerms } = useConfigLookup('payment_terms');
    const { options: depositOptions } = useConfigLookup('deposit_options');
    const { options: currencies } = useConfigLookup('currencies');
    
    const paymentLabel = paymentTerms.find(o => o.id === order.paymentTerm)?.label || order.paymentTerm || 'Theo hợp đồng';
    const depositLabel = depositOptions.find(o => o.id === order.deposit)?.label || order.deposit || 'Không';
    
    const rawCurrency = (order as any).currency || 'VND';
    const currencyLabel = currencies.find(o => o.id === rawCurrency)?.label || rawCurrency;
    const currency = rawCurrency;

    // Financial calculations
    const exchangeRate = Number((order as any).exchangeRate) || 1;
    const isVND = currency === 'VND';
    const isUsdOrder = String(currency).toUpperCase() === 'USD';
    // Thành tiền dòng — đúng cách tính cột "Thành tiền" trên bảng (dòng cũ có thể lưu thành tiền = 0)
    const lineAmountOf = (d: any) => {
        const qty = Number(d.quantity) || 0;
        const price = isUsdOrder && d.priceUSD != null && Number(d.priceUSD) > 0
            ? Number(d.priceUSD)
            : (Number(d.unitPrice) || Number(d.priceVND) || 0);
        return Number(d.amount) || qty * price;
    };
    // Cộng tiền hàng = tổng cột Thành tiền (trước đây cộng thành tiền đã lưu = 0 → in ra 0, FB-B19MUI)
    const amount = details.reduce((sum, d) => sum + lineAmountOf(d), 0);
    const vatRate = Number(order.vatRate) || 0;
    const vatAmount = Number(order.vatAmount) || (amount * vatRate / 100);
    const shippingFee = Number((order as any).shippingFee) || 0;
    const totalAmount = Number(order.totalAmount) || (amount + vatAmount + shippingFee);

    // Format helpers
    // Format helpers — unused vars removed, using inline toLocaleString

    return (
        <div className="p-[10mm] text-black leading-snug text-[12px] bg-white w-full mx-auto min-h-[297mm]" style={{ fontFamily: "'Times New Roman', Times, serif" }} id="print-area">
            <style>{`
                /* No inline media print styles needed, managed by PrintDialog */
            `}</style>
            
            {/* ========== COMPANY HEADER ========== */}
            <table className="w-full border-collapse mb-4" style={{ border: "2px solid #000" }}>
                <tbody>
                    <tr>
                        <td className="w-[20%] text-center align-middle border border-black p-2">
                            {c.logoUrl ? (
                                <img src={c.logoUrl} alt="Logo" className="max-h-[45px] mx-auto object-contain" />
                            ) : (
                                <span className="font-bold text-[14px] uppercase">{c.companyName}</span>
                            )}
                        </td>
                        <td className="w-[80%] border border-black p-2 text-center align-middle">
                            <div className="font-bold text-[13px] uppercase">{c.companyName}</div>
                            <div className="text-[10px] mt-0.5">{c.address1 || c.address2}</div>
                            <div className="text-[10px] mt-0.5">MST: {c.taxCode} | ĐT: {c.phone} | Email: {c.email}</div>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* ========== DOCUMENT TITLE (CENTERED, NO QR) ========== */}
            <PrintDocumentTitle
                title="ĐƠN ĐẶT HÀNG"
                subtitle="Purchase Order"
            />

            {/* ========== ORDER INFO GRID (CLEAN 2-COLUMN TABS) ========== */}
            <PrintInfoGrid
                leftColumn={[
                    {
                        label: "Khách hàng",
                        labelEn: "Customer",
                        value: order.customer?.name || '---',
                        valueClassName: "text-[12px]",
                    },
                    {
                        label: "Người liên hệ",
                        labelEn: "Contact",
                        value: order.contactPerson || '---',
                    },
                    {
                        label: "NV Sales",
                        labelEn: "Sales Rep",
                        value: order.salesUser?.fullName || 'Admin',
                    },
                ]}
                rightColumn={[
                    {
                        label: "Số ĐH",
                        labelEn: "Order No.",
                        value: order.orderId,
                        valueClassName: "text-blue-700 font-mono",
                    },
                    {
                        label: "Số PO khách",
                        labelEn: "PO No.",
                        value: order.orderDetails?.[0]?.poReference || '---',
                    },
                    {
                        label: "Ngày đặt",
                        labelEn: "Order date",
                        value: formatDate(order.orderDate),
                    },
                    {
                        label: "Hạn giao",
                        labelEn: "Deadline",
                        value: order.deliveryNotes || (order.deliveryDeadline ? formatDate(order.deliveryDeadline) : 'Theo thỏa thuận'),
                        valueClassName: "text-red-700 font-bold",
                    },
                    {
                        label: "Loại tiền",
                        labelEn: "Currency",
                        value: isVND ? currencyLabel : `${currencyLabel} (Tỷ giá: ${exchangeRate.toLocaleString('vi-VN')} VND)`,
                        valueClassName: "text-blue-700",
                    },
                ]}
                fullWidthItems={[
                    {
                        label: "Địa chỉ giao",
                        labelEn: "Delivery",
                        value: order.deliveryAddress || order.customer?.address || '---',
                        colSpan: 2,
                    },
                ]}
            />


            {/* ========== PRODUCT TABLE ========== */}
            {(() => {
                const parsedDetails = details.map((d: any) => {
                    let parsedSpecs: any = {};
                    if (d.product?.specs) {
                        try {
                            parsedSpecs = typeof d.product.specs === 'string' ? JSON.parse(d.product.specs) : d.product.specs;
                        } catch { /* ignore */ }
                    }
                    return {
                        ...d,
                        parsedSpecs
                    };
                });

                const normalized = parsedDetails.map((d: any) => ({
                    ...d,
                    _sortDesc: (d.product?.productProcesses?.map((p: any) => p.process?.name).filter(Boolean).join(', ') || d.product?.process || d.process || '').toLowerCase().trim(),
                    _sortName: (d.productName || d.productCode || '').toLowerCase().trim(),
                    _sortSpec: parseSpecs(d.specs),
                    _sortPoSo: `${d.poReference || ''}-${d.soReference || ''}`,
                    // Dòng khác ghi chú không gộp chung ô (mỗi ghi chú phải in ra)
                    _sortNote: String(d.notes || '').trim(),
                    // Hai sản phẩm khác mã nhưng trùng tên (VD BP-274VN-JP / BP-275VN-JP) không được gộp chung ô sản phẩm
                    _sortProduct: d.productId || d.product?.id || d.productCode || d.product?.productCode || '',
                }));

                const sorted = normalized; // Use original sortOrder instead of alphabetic sort

                const spanDetails: number[] = new Array(sorted.length).fill(0);
                let gi = 0;
                while (gi < sorted.length) {
                    let gj = gi + 1;
                    while (gj < sorted.length && sorted[gj]._sortDesc === sorted[gi]._sortDesc) gj++;
                    spanDetails[gi] = gj - gi;
                    gi = gj;
                }

                const spanProduct: number[] = new Array(sorted.length).fill(0);
                gi = 0;
                while (gi < sorted.length) {
                    let gj = gi + 1;
                    while (
                        gj < sorted.length &&
                        sorted[gj]._sortDesc === sorted[gi]._sortDesc &&
                        sorted[gj]._sortName === sorted[gi]._sortName &&
                        sorted[gj]._sortProduct === sorted[gi]._sortProduct &&
                        sorted[gj]._sortSpec === sorted[gi]._sortSpec &&
                        sorted[gj]._sortPoSo === sorted[gi]._sortPoSo &&
                        sorted[gj]._sortNote === sorted[gi]._sortNote
                    ) gj++;
                    spanProduct[gi] = gj - gi;
                    gi = gj;
                }

                const showImagesCol = !!showImages;
                const showEquivCol = !isVND;

                const remaining = 56 - (showImagesCol ? 10 : 0) - (showEquivCol ? 12 : 0);
                const productWidth = `${Math.floor(remaining * 0.46)}%`;
                const descWidth = `${remaining - Math.floor(remaining * 0.46)}%`;

                const extraCols = (showImages ? 1 : 0) + (!isVND ? 1 : 0);
                const baseColCount = 7 + extraCols; // STT + Name + Material + [Image] + Unit + Qty + Price + Amount + [VND]
                const footerSpan = 5 + (showImages ? 1 : 0);

                return (
                    <table className="w-full border-collapse border border-black mb-1 text-[11px] table-fixed">
                        <thead>
                            <tr className="bg-slate-100 font-bold text-center">
                                <th className="border border-black px-1 py-1.5" style={{ width: '4%' }}>STT<br /><i className="font-normal">No.</i></th>
                                <th className="border border-black px-1.5 py-1.5 text-left" style={{ width: productWidth }}>Tên sản phẩm<br /><i className="font-normal">Product name &amp; Specs</i></th>
                                <th className="border border-black px-1.5 py-1.5 text-left" style={{ width: descWidth }}>Chi tiết<br /><i className="font-normal">Description</i></th>
                                {showImagesCol && (
                                    <th className="border border-black px-1 py-1.5" style={{ width: '10%' }}>Hình<br /><i className="font-normal">Image</i></th>
                                )}
                                <th className="border border-black px-1 py-1.5 whitespace-nowrap" style={{ width: '7%' }}>ĐVT<br /><i className="font-normal">Unit</i></th>
                                <th className="border border-black px-1 py-1.5 whitespace-nowrap" style={{ width: '9%' }}>SL<br /><i className="font-normal">Q'ty</i></th>
                                <th className="border border-black px-1 py-1.5 whitespace-nowrap" style={{ width: '11%' }}>Đơn giá{!isVND && ` (${currency})`}<br /><i className="font-normal">Price</i></th>
                                <th className="border border-black px-1 py-1.5 whitespace-nowrap" style={{ width: '13%' }}>Thành tiền{!isVND && ` (${currency})`}<br /><i className="font-normal">Amount</i></th>

                                {showEquivCol && (
                                    <th className="border border-black px-1 py-1.5 bg-yellow-50 whitespace-nowrap" style={{ width: '12%' }}>Quy đổi (VND)<br /><i className="font-normal">VND Equiv.</i></th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.length === 0 ? (
                                <tr>
                                    <td colSpan={baseColCount} className="border border-black p-4 italic text-slate-500 text-center">
                                        Chưa có sản phẩm nào
                                    </td>
                                </tr>
                            ) : (
                                sorted.map((item: any, index: number) => {
                                    const qty = Number(item.quantity) || 0;
                                    const isUsd = (order.currency || '').toUpperCase() === 'USD';
                                    const unitPrice = isUsd && item.priceUSD != null && Number(item.priceUSD) > 0
                                        ? Number(item.priceUSD)
                                        : (Number(item.unitPrice) || Number(item.priceVND) || 0);
                                    const lineAmount = lineAmountOf(item);
                                    const productName = item.productName || '';
                                    const productGroup = item.productGroup || item.product?.productType || '';
                                    const specsText = parseSpecs(item.specs || item.product?.specs);
                                    // Chất liệu trước, rồi các công đoạn (VD "Ivory 300gsm, In Offset, Tráng phủ...")
                                    const processText = cleanProcessText(orderLineDetailText(item), specsText);
                                    const showMergeProduct = spanProduct[index] > 0;
                                    const showMergeDetails = spanDetails[index] > 0;

                                    return (
                                        <tr key={item.id || index} className="break-inside-avoid">
                                            <td className="border border-black px-1 py-1 text-center">{index + 1}</td>
                                            {showMergeProduct && (
                                                <td className="border border-black px-1.5 py-1 text-left align-top break-words" rowSpan={spanProduct[index]}>
                                                    {productGroup && (
                                                        <span className="font-normal">{productGroup}</span>
                                                    )}
                                                    {productGroup ? ' - ' : ''}
                                                    <span className="font-bold">
                                                        {productName}
                                                    </span>
                                                    {item.productCode && (
                                                        <span className="text-slate-400 font-mono ml-1">[{item.productCode}]</span>
                                                    )}
                                                    {specsText && (
                                                        <div className="italic text-slate-600 mt-0.5 font-normal">{specsText}</div>
                                                    )}
                                                    {item.deliveryDate && (
                                                        <div className="mt-0.5 text-blue-700 italic">Giao: {formatDate(item.deliveryDate)}</div>
                                                    )}
                                                    {(item.poReference || item.soReference) && (
                                                        <div className="mt-1 flex gap-2 text-[10px] font-mono text-slate-700">
                                                            {item.poReference && <span className="bg-slate-100 px-1 border border-slate-200 rounded">PO: {item.poReference}</span>}
                                                            {item.soReference && <span className="bg-slate-100 px-1 border border-slate-200 rounded">SO: {item.soReference}</span>}
                                                        </div>
                                                    )}
                                                    {item.notes && (
                                                        <div className="mt-0.5 text-slate-800 whitespace-pre-wrap" data-testid="order-print-line-note">Ghi chú: {item.notes}</div>
                                                    )}
                                                </td>
                                            )}
                                            {showMergeDetails && (
                                                <td className="border border-black px-1.5 py-1 text-left align-top whitespace-pre-wrap break-words" rowSpan={spanDetails[index]}>
                                                    {processText || <span className="text-slate-300">—</span>}
                                                </td>
                                            )}
                                            {showImages && showMergeProduct && (
                                                <td className="border border-black px-1 py-1 text-center align-middle" rowSpan={spanProduct[index]}>
                                                    {(item.product?.image || item.image) ? (
                                                        <img src={item.product?.image || item.image} alt={productName} className="w-[60px] h-[60px] object-contain mx-auto" />
                                                    ) : (
                                                        <span className="text-slate-300">—</span>
                                                    )}
                                                </td>
                                            )}
                                            {showMergeProduct && (
                                                <td className="border border-black px-1 py-1 text-center text-[10px] whitespace-nowrap" rowSpan={spanProduct[index]}>{item.unit || 'Cái'}</td>
                                            )}
                                            <td className="border border-black px-1 py-1 text-center font-bold whitespace-nowrap tabular-nums">{qty.toLocaleString('vi-VN')}</td>
                                            <td className="border border-black px-1 py-1 text-right tabular-nums whitespace-nowrap">{unitPrice.toLocaleString('vi-VN')}</td>
                                            <td className="border border-black px-1 py-1 text-right font-bold tabular-nums whitespace-nowrap">{lineAmount.toLocaleString('vi-VN')}</td>
                                            {!isVND && (
                                                <td className="border border-black px-1 py-1 text-right tabular-nums bg-yellow-50 font-bold whitespace-nowrap">{(lineAmount * exchangeRate).toLocaleString('vi-VN')}</td>
                                            )}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                        {/* ========== FINANCIAL SUMMARY ========== */}
                        <tfoot>
                            <tr className="bg-slate-50">
                                <td colSpan={footerSpan} className="border border-black px-2 py-1 text-left font-bold">
                                    Cộng tiền hàng/ <i>Subtotal</i>:
                                </td>
                                <td colSpan={2} className="border border-black px-2 py-1 text-right font-bold tabular-nums" data-testid="order-print-subtotal">
                                    {amount.toLocaleString('vi-VN')} {isVND ? '₫' : currency}
                                </td>
                                {!isVND && (
                                    <td className="border border-black px-2 py-1 text-right font-bold tabular-nums bg-yellow-50">
                                        {(amount * exchangeRate).toLocaleString('vi-VN')} ₫
                                    </td>
                                )}
                            </tr>
                            {vatRate > 0 && (
                                <tr>
                                    <td colSpan={footerSpan} className="border border-black px-2 py-1 text-left">
                                        Thuế GTGT/ <i>VAT</i> ({vatRate}%):
                                    </td>
                                    <td colSpan={2} className="border border-black px-2 py-1 text-right tabular-nums">
                                        {vatAmount.toLocaleString('vi-VN')} {isVND ? '₫' : currency}
                                    </td>
                                    {!isVND && (
                                        <td className="border border-black px-2 py-1 text-right tabular-nums bg-yellow-50">
                                            {(vatAmount * exchangeRate).toLocaleString('vi-VN')} ₫
                                        </td>
                                    )}
                                </tr>
                            )}
                            {shippingFee > 0 && (
                                <tr data-testid="order-print-shipping-row">
                                    <td colSpan={footerSpan} className="border border-black px-2 py-1 text-left">
                                        Chi phí vận chuyển/ <i>Shipping fee</i>:
                                    </td>
                                    <td colSpan={2} className="border border-black px-2 py-1 text-right tabular-nums">
                                        {shippingFee.toLocaleString('vi-VN')} {isVND ? '₫' : currency}
                                    </td>
                                    {!isVND && (
                                        <td className="border border-black px-2 py-1 text-right tabular-nums bg-yellow-50">
                                            {(shippingFee * exchangeRate).toLocaleString('vi-VN')} ₫
                                        </td>
                                    )}
                                </tr>
                            )}
                            <tr className="bg-yellow-50">
                                <td colSpan={footerSpan} className="border border-black px-2 py-1.5 text-left font-bold text-[13px]">
                                    TỔNG GIÁ TRỊ/ <i>TOTAL</i>:
                                </td>
                                <td colSpan={2} className="border border-black px-2 py-1.5 text-right font-bold text-[13px] text-red-700 tabular-nums">
                                    {totalAmount.toLocaleString('vi-VN')} {isVND ? '₫' : currency}
                                </td>
                                {!isVND && (
                                    <td className="border border-black px-2 py-1.5 text-right font-bold text-[13px] text-red-700 tabular-nums">
                                        {(totalAmount * exchangeRate).toLocaleString('vi-VN')} ₫
                                    </td>
                                )}
                            </tr>
                            <tr>
                                <td colSpan={baseColCount} className="border border-black px-2 py-1 text-[11px] italic">
                                    <b>Bằng chữ/ <i>In words</i>:</b>{' '}
                                    {isVND
                                        ? numberToVietnameseWords(totalAmount, 'đồng')
                                        : <>{numberToVietnameseWords(totalAmount, currency)} <span className="text-slate-500">(Quy đổi: {numberToVietnameseWords(totalAmount * exchangeRate, 'đồng')})</span></>
                                    }
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                );
            })()}

            {/* ========== NOTES + TERMS ========== */}
            <div className="grid grid-cols-2 gap-6 text-[11px] mt-6 mb-8">
                <div>
                    <h3 className="font-bold mb-1.5 border-b border-black pb-0.5 inline-block">
                        Điều khoản & Ghi chú/ <i>Terms & Notes</i>:
                    </h3>
                    <ol className="list-decimal pl-5 space-y-1 text-[10px] leading-[14px]">
                        <li>
                            Giá {vatRate > 0 ? `đã bao gồm ${vatRate}%` : 'chưa bao gồm'} VAT/
                            <i> Price {vatRate > 0 ? `includes ${vatRate}%` : 'excludes'} VAT</i>
                        </li>
                        <li>
                            Phương thức thanh toán/ <i>Payment</i>: {paymentLabel}
                            {order.deposit && <><br />Đặt cọc/ <i>Deposit: {depositLabel}</i></>}
                        </li>
                        <li>
                            Thời gian giao hàng/ <i>Delivery</i>: {order.deliveryNotes || 'Theo thỏa thuận và kế hoạch của các bên'}
                        </li>
                        <li>Số lượng giao hàng cho phép sai lệch/ <i>Tolerance</i>: ±{order.tolerancePercent ?? order.quantityTolerance ?? order.customer?.tolerancePercent ?? 10}%</li>
                        {order.notes && (
                            <li className="font-bold text-red-700">
                                Ghi chú/ <i>Note</i>: {order.notes}
                            </li>
                        )}
                    </ol>
                </div>

                <div>
                    <h3 className="font-bold mb-1.5 border-b border-black pb-0.5 inline-block">
                        THÔNG TIN THANH TOÁN/ <i>PAYMENT INFO</i>:
                    </h3>
                    <div className="text-[10px] space-y-1 leading-[14px]">
                        <p>Tài khoản (VND)/ <i>Bank account</i>: <b>{c.bankAccount}</b></p>
                        <p>Tên Ngân hàng/ <i>Bank name</i>: <b>{c.bankName}</b></p>
                        <p>Tên tài khoản/ <i>Beneficiary</i>: <b>{c.beneficiary}</b></p>
                    </div>
                </div>
            </div>

            {/* ========== FOOTER SIGNATURES ========== */}
            <div className="flex justify-between mt-6 text-[11px] break-inside-avoid">
                {/* Left: Company (Bên B - Nhà sản xuất) */}
                <div className="text-center w-[45%]">
                    <div className="italic mb-1 text-[10px]">
                        Ngày {String(now.getDate()).padStart(2, '0')} tháng {String(now.getMonth() + 1).padStart(2, '0')} năm {now.getFullYear()}
                    </div>
                    <div className="font-bold mb-1">BÊN B (NHÀ SẢN XUẤT)</div>
                    <div className="font-bold uppercase text-[10px] text-slate-600 mb-16">{c.companyNameEn}</div>
                    <div className="border-t border-dashed border-slate-400 pt-1 mx-8">
                        <div className="text-[10px] text-slate-500">Ký tên, đóng dấu/ <i>Sign & Stamp</i></div>
                    </div>
                </div>

                {/* Right: Customer (Bên A - Khách hàng) */}
                <div className="text-center w-[45%]">
                    <div className="italic mb-1 text-[10px]">&nbsp;</div>
                    <div className="font-bold mb-1">BÊN A (KHÁCH HÀNG)</div>
                    <div className="font-bold uppercase text-[10px] text-slate-600 mb-16">{order.customer?.name || ''}</div>
                    <div className="border-t border-dashed border-slate-400 pt-1 mx-8">
                        <div className="text-[10px] text-slate-500">Ký tên, đóng dấu/ <i>Sign & Stamp</i></div>
                    </div>
                </div>
            </div>
        </div>
    );
}
